/*
  Upsell dropdown — swatch list skin for upsell variant pickers.

  Loaded from layout/theme.liquid only when Theme settings → Variant dropdowns
  → "Upsell dropdown style" is 'swatch'. Applies to pickers marked
  [data-upsell-dropdown] (snippets/upsell-block.liquid and
  snippets/upsell-item-card.liquid). The bundle offer and cart gift share the
  .upsell__variant-picker class but carry no marker, so they are untouched.

  The native <select> stays the single source of truth. main.js rewrites its
  <option>s after every pick and sets select.value directly; cart-upsell-scope.js
  restores select.value after a drawer re-render; upsell-variant-sync.js sets it
  and dispatches 'change'; CartDrawerUpsell disables it mid-request. All of that
  keeps working because this file never replaces the select: it covers it with
  a click target, reads the live <option>s each time the list opens, and picks
  by setting select.value + dispatching 'input'/'change' like a native pick.
  The select stays focusable, so keyboard and screen-reader use is native.

  The Product variants block's dropdowns opt in with [data-vp-swatch-dropdown]
  on their .select wrapper (snippets/product-variant-picker.liquid, "Custom"
  dropdown style). <variant-selects> reads the same select + 'change' event.
*/
(function () {
  if (window.__upsellDropdown) return;
  window.__upsellDropdown = true;

  var SELECTOR = '[data-upsell-dropdown] select.variant-dropdown, [data-vp-swatch-dropdown] > select.select__select';
  var VALUE = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
  var INDEX = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'selectedIndex');
  var current = null; /* { select, wrap, list } while a list is open */

  function selectedOption(select) {
    return select.options[select.selectedIndex] || null;
  }

  /* Swatch dot on the closed picker follows the selected option. */
  function refresh(select, wrap) {
    var option = selectedOption(select);
    var swatch = option && option.getAttribute('data-swatch');
    var dot = wrap.querySelector(':scope > .uvd__dot');
    if (!dot) return;
    if (swatch) {
      var h = select.offsetHeight;
      if (h) wrap.style.setProperty('--uvd-dot', Math.round(Math.min(Math.max(h * 0.6, 10), 22)) + 'px');
      dot.style.background = swatch;
      wrap.classList.add('uvd--swatch');
    } else {
      dot.style.background = '';
      wrap.classList.remove('uvd--swatch');
    }
    if (current && current.select === select) render(current.list, select);
  }

  /* Catch the silent select.value / selectedIndex writes other scripts make. */
  function watchWrites(select, wrap) {
    try {
      Object.defineProperty(select, 'value', {
        configurable: true,
        get: function () { return VALUE.get.call(this); },
        set: function (v) { VALUE.set.call(this, v); refresh(select, wrap); }
      });
      Object.defineProperty(select, 'selectedIndex', {
        configurable: true,
        get: function () { return INDEX.get.call(this); },
        set: function (v) { INDEX.set.call(this, v); refresh(select, wrap); }
      });
    } catch (e) { /* refresh still runs on change and option mutations */ }
  }

  function enhance(select) {
    if (select.__uvd) return;
    var wrap = select.closest('.select') || select.parentElement;
    if (!wrap) return;
    select.__uvd = true;

    /* A re-render that copied an enhanced picker's HTML brings dead nodes. */
    wrap.querySelectorAll(':scope > .uvd__dot, :scope > .uvd__hit').forEach(function (n) { n.remove(); });

    var dot = document.createElement('span');
    dot.className = 'uvd__dot';
    dot.setAttribute('aria-hidden', 'true');
    var hit = document.createElement('span');
    hit.className = 'uvd__hit';
    hit.setAttribute('aria-hidden', 'true');
    wrap.appendChild(dot);
    wrap.appendChild(hit);
    wrap.classList.add('uvd');

    hit.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      if (current && current.select === select) return close();
      if (!select.disabled) open(select, wrap);
    });
    select.addEventListener('change', function () { refresh(select, wrap); });
    new MutationObserver(function () { refresh(select, wrap); })
      .observe(select, { subtree: true, childList: true, attributes: true, characterData: true });

    watchWrites(select, wrap);
    refresh(select, wrap);
  }

  function render(list, select) {
    list.textContent = '';
    /* Keep labels aligned when only some values have a color. */
    var anySwatch = !!select.querySelector('option:not([hidden])[data-swatch]');
    Array.prototype.forEach.call(select.options, function (option, index) {
      if (option.hidden) return;
      var item = document.createElement('div');
      item.className = 'uvd-item';
      if (index === select.selectedIndex) item.classList.add('is-selected');
      if (option.disabled) item.classList.add('is-disabled');
      if (option.classList.contains('unavailable')) item.classList.add('is-unavailable');

      var swatch = option.getAttribute('data-swatch');
      if (anySwatch) {
        var dot = document.createElement('span');
        dot.className = 'uvd-item__dot' + (swatch ? '' : ' uvd-item__dot--none');
        if (swatch) dot.style.background = swatch;
        item.appendChild(dot);
      }
      var label = document.createElement('span');
      label.className = 'uvd-item__label';
      label.textContent = option.textContent.trim();
      item.appendChild(label);

      item.addEventListener('click', function (event) {
        event.preventDefault();
        if (option.disabled || select.disabled) return;
        choose(select, index);
      });
      list.appendChild(item);
    });
  }

  function choose(select, index) {
    close();
    if (select.selectedIndex === index) return;
    select.selectedIndex = index;
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function themeColors(list, select, wrap) {
    var cs = getComputedStyle(select);
    var bg = getComputedStyle(wrap).getPropertyValue('--color-background').trim();
    list.style.background = bg ? 'rgb(' + bg + ')' : '#fff';
    list.style.color = cs.color;
    list.style.fontFamily = cs.fontFamily;
    list.style.fontSize = Math.max(parseFloat(cs.fontSize) || 0, 15) + 'px';
    /* The list lives on <body>, so the block's swatch settings don't cascade. */
    var ws = getComputedStyle(wrap);
    ['--uvd-swatch-size', '--uvd-swatch-radius', '--uvd-swatch-gap', '--uvd-row-pad'].forEach(function (name) {
      var v = ws.getPropertyValue(name).trim();
      if (v) list.style.setProperty(name, v);
    });
  }

  function position() {
    if (!current) return;
    var list = current.list;
    var rect = current.wrap.getBoundingClientRect();
    var vh = window.innerHeight;
    var vw = document.documentElement.clientWidth;
    var below = vh - rect.bottom - 8;
    var above = rect.top - 8;
    var up = below < 200 && above > below;

    list.style.minWidth = Math.max(rect.width, 160) + 'px';
    list.style.maxHeight = Math.max(Math.min(up ? above : below, 360), 120) + 'px';
    list.style.top = up ? '' : rect.bottom + 4 + 'px';
    list.style.bottom = up ? vh - rect.top + 4 + 'px' : '';
    var left = Math.min(rect.left, vw - list.offsetWidth - 8);
    list.style.left = Math.max(left, 8) + 'px';

    var selected = list.querySelector('.is-selected');
    if (selected && !current.scrolled) {
      current.scrolled = true;
      list.scrollTop = selected.offsetTop - list.clientHeight / 2 + selected.offsetHeight / 2;
    }
  }

  function open(select, wrap) {
    close();
    var list = document.createElement('div');
    list.className = 'uvd-list';
    list.setAttribute('aria-hidden', 'true');
    /* Keep clicks inside the list from reaching drawer "click outside" handlers. */
    ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach(function (type) {
      list.addEventListener(type, function (event) { event.stopPropagation(); });
    });
    themeColors(list, select, wrap);
    render(list, select);
    document.body.appendChild(list);
    current = { select: select, wrap: wrap, list: list, scrolled: false };
    wrap.classList.add('uvd--open');
    position();
  }

  function close() {
    if (!current) return;
    current.list.remove();
    current.wrap.classList.remove('uvd--open');
    current = null;
  }

  document.addEventListener('pointerdown', function (event) {
    if (!current) return;
    if (current.list.contains(event.target) || current.wrap.contains(event.target)) return;
    close();
  }, true);
  document.addEventListener('keydown', function (event) {
    if (current && event.key === 'Escape') close();
  }, true);
  document.addEventListener('scroll', function (event) {
    if (!current || event.target === current.list) return;
    close();
  }, true);
  window.addEventListener('resize', close);

  function scan(root) {
    if (root.matches && root.matches(SELECTOR)) enhance(root);
    if (root.querySelectorAll) root.querySelectorAll(SELECTOR).forEach(enhance);
  }

  var pending = false;
  new MutationObserver(function () {
    if (current && !document.contains(current.wrap)) close();
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      scan(document);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
})();
